import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { hash, array } from '@ember/helper';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, and, not, or } from 'ember-truth-helpers';
import { variation } from 'ember-launch-darkly';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import can from 'ember-can/helpers/can';
import set from 'ember-set-helper/helpers/set';
import {
  HdsButton,
  HdsLinkStandalone,
  HdsTable,
  HdsIcon,
  HdsBadge,
  HdsLinkInline,
  HdsDropdown,
  HdsModal,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import { Typography, Flex, ZeroState } from 'core/components';
import {
  resourceIsBusy,
  resourceTypeFor,
  optionForSourceState,
} from 'core/helpers';
import { tooltip, boxPadding } from 'core/modifiers';
import { AWS_CONNECTIONS_INFO } from 'networks-common/utils/aws-connections';
import { HVN_ROUTE_NAMES } from 'networks-common/utils/network-routes';
import TargetTypeLabel from 'hcp/components/page/hvn-routes/table/target-type-label';

/**
 * `connection` is derived from the list and passed down.
 * @argument model;
 * @type {Object}
 *
 * `isHvnHvnPeering` signifies if the routes are foran hvn-hvn peering.
 * @argument isHvnHvnPeering;
 * @type {Boolean}
 *
 * `model` is the model sent from a connection type or from the routes tab within a network
 * @argument model;
 * @type {Object}
 */
/**
 *
 * `PageHvnRoutesTable` renders the table, zero-state, and modal for routes.
 *
 *
 * ```
 * <Page::HvnRoutes::Table
 *   @connection={{this.connection}}
 *   @model={{@model}}
 *   @isHvnHvnPeering={{true|false}}
 * />
 * ```
 *
 * @class PageHvnRoutesList
 *
 */
export default class PageHvnRoutesList extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service operation;
  @service router;

  @tracked isOpen;
  @tracked showDeleteModal = false;
  @tracked allowDeleteRoute = false;
  @tracked hvnRouteToDelete;
  @tracked routeName = this.router.currentRouteName;

  hvnRouteNames = HVN_ROUTE_NAMES;
  connectionsInfo = AWS_CONNECTIONS_INFO;

  get connection() {
    const { connection } = this.args;
    return connection;
  }

  get connectionCreateHVN() {
    if (this.connection.type === AWS_CONNECTIONS_INFO.peering.type) {
      return AWS_CONNECTIONS_INFO.peering.createRoute;
    }
    if (this.connection.type === AWS_CONNECTIONS_INFO.tgwAttachment.type) {
      return AWS_CONNECTIONS_INFO.tgwAttachment.createRoute;
    }
    return null;
  }

  get zeroStateMessage() {
    if (this.connection?.type === AWS_CONNECTIONS_INFO.peering.type) {
      return this.intl.t(
        'components.page.hvn-routes.list.empty.target.peering.message',
        {
          provider: this.args.model.network.location.region.provider,
        }
      );
    }
    if (this.connection?.type === AWS_CONNECTIONS_INFO.tgwAttachment.type) {
      return this.intl.t(
        'components.page.hvn-routes.list.empty.target.tgw-attachment.message',
        {
          provider: this.args.model.network.location.region.provider,
        }
      );
    }
    return null;
  }

  setOpen = (evt) => {
    this.isOpen = document.getElementById(evt.target.id).open;
  };

  deleteHvnRoute = task(async (evt) => {
    evt.preventDefault();
    let { organizationId, projectId } = this.args.model.network.location;
    let hvnId = this.args.model.network.id;
    let { id } = this.hvnRouteToDelete;
    let operation;
    try {
      let resp = await this.api.network.deleteHVNRoute(
        organizationId,
        projectId,
        hvnId,
        id
      );
      this.flashMessages.success(
        this.intl.t('components.page.hvn-routes.delete.success.title')
      );
      operation = resp.operation;
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      throw e;
    }
    this.showDeleteModal = false;
    this.operation.addOperation(operation);
  });

  <template>
    <div class="hvnsRoutesList" data-test-hvn-routes-list-container>
      {{! Target list view is within a Peering Connection or TGW detail page. }}
      {{#if this.connection}}
        <div {{boxPadding "xl 0 0 0"}}>
          <header class="detailSection__header">
            <Typography @variant="h2">
              {{if
                (eq this.routeName this.hvnRouteNames.list)
                (t "components.page.hvn-routes.list.title")
                (t "components.page.hvn-routes.list.title-short")
              }}
            </Typography>
          </header>
        </div>
      {{/if}}
      {{#if @model.routes.length}}
        {{#if
          (and
            this.connection
            (not
              (or
                @isHvnHvnPeering
                (eq this.connection.state "EXPIRED")
                (eq this.connection.state "REJECTED")
                (eq this.connection.state "FAILED")
              )
            )
            (can "create network-route")
          )
        }}
          <Flex @direction="row" @justifyContent="end" {{boxPadding "xs 0"}}>
            <HdsButton
              @color="tertiary"
              @icon="plus"
              @iconPosition="leading"
              @route={{this.connection.createRoute}}
              @text={{t "components.page.hvn-routes.create.title"}}
              data-test-create
            />
          </Flex>
        {{/if}}
        {{#if (variation "hcp-hvn-guided-flow")}}
          <Typography
            class="hds-typography-body-100 hds-font-weight-regular hds-foreground-faint"
          >
            {{t "components.page.hvn-routes.list.description"}}
          </Typography>
          <HdsLinkStandalone
            @text={{t "components.page.hvn-routes.list.learn-link-text"}}
            @href={{t
              "components.page.hvn-routes.list.learn-link-url"
              provider=@model.network.location.region.provider
            }}
            @icon="learn-link"
            @iconPosition="trailing"
            data-test-hvn-routes-list-learn-link
            {{boxPadding "md 0 lg 0"}}
          />
          <HdsTable
            @valign="middle"
            @caption={{t "components.page.hvn-routes.list.title"}}
            data-test-hvn-routes-list-table
          >
            <:head as |H|>
              <H.Tr>
                <H.Th>
                  {{t "components.page.hvn-routes.list.table.headers.route-id"}}
                </H.Th>
                <H.Th>
                  {{t "components.page.hvn-routes.list.table.headers.status"}}
                </H.Th>
                {{#if (eq this.routeName this.hvnRouteNames.list)}}
                  <H.Th>
                    {{t
                      "components.page.hvn-routes.list.table.headers.destination"
                    }}
                  </H.Th>
                {{/if}}
                <H.Th>
                  {{t "components.page.hvn-routes.list.table.headers.target"}}
                </H.Th>
                {{#if (eq this.routeName this.hvnRouteNames.list)}}
                  <H.Th>
                    {{t
                      "components.page.hvn-routes.list.table.headers.target-type"
                    }}
                  </H.Th>
                {{/if}}
                <H.Th />
              </H.Tr>
            </:head>
            <:body as |B|>
              {{#each @model.routes key="id" as |route|}}
                <B.Tr data-test-hvn-routes-list-item>
                  <B.Td data-test-hvn-routes-list-item-id>
                    <Flex @alignItems="center" @direction="row" @gap="2xs">
                      <span>
                        {{#if
                          (eq
                            route.target.hvnConnection.type
                            (resourceTypeFor "network")
                          )
                        }}
                          {{t
                            "components.page.hvn-routes.list.network-default-route-id"
                          }}
                        {{else}}
                          {{route.id}}
                        {{/if}}
                      </span>
                      {{#if
                        (or
                          route.isLocked
                          (eq
                            route.target.hvnConnection.type
                            (resourceTypeFor "network")
                          )
                        )
                      }}
                        <HdsIcon
                          @name="lock-fill"
                          @isInline={{true}}
                          data-test-peering-locked
                        />
                      {{/if}}
                    </Flex>
                  </B.Td>
                  <B.Td data-test-hvn-routes-list-item-state>
                    {{#let
                      (optionForSourceState "hvn_route" route.state)
                      as |option|
                    }}
                      <HdsBadge
                        @text={{t option.text}}
                        @color={{option.color}}
                        @icon={{option.icon}}
                        @isIconOnly={{option.isIconOnly}}
                        @size={{option.size}}
                        @type={{option.type}}
                      />
                    {{/let}}
                  </B.Td>
                  <B.Td>
                    {{route.destination}}
                  </B.Td>
                  {{#if (eq this.routeName this.hvnRouteNames.list)}}
                    <B.Td data-test-hvn-routes-list-item-names>
                      {{! TODO there has to be a way to put this into some sort of helper  }}
                      {{#if route.isLocked}}
                        {{route.target.hvnConnection.id}}
                      {{else}}
                        {{#if
                          (eq
                            route.target.hvnConnection.type
                            this.connectionsInfo.tgwAttachment.type
                          )
                        }}
                          <HdsLinkInline
                            @model={{route.target.hvnConnection.id}}
                            @route={{this.connectionsInfo.tgwAttachment.detail}}
                          >
                            {{route.target.hvnConnection.id}}
                          </HdsLinkInline>
                        {{else if
                          (eq
                            route.target.hvnConnection.type
                            this.connectionsInfo.peering.type
                          )
                        }}
                          <HdsLinkInline
                            @model={{route.target.hvnConnection.id}}
                            @route={{this.connectionsInfo.peering.detail}}
                          >
                            {{route.target.hvnConnection.id}}
                          </HdsLinkInline>
                        {{else}}
                          {{route.target.hvnConnection.id}}
                        {{/if}}
                      {{/if}}
                    </B.Td>
                  {{/if}}
                  {{#if (eq this.routeName this.hvnRouteNames.list)}}
                    <B.Td data-test-hvn-routes-list-item-type>
                      <TargetTypeLabel
                        @route={{route}}
                        @network={{@model.network}}
                      />
                    </B.Td>
                  {{/if}}
                  <B.Td>
                    {{#if
                      (not
                        (or
                          (eq
                            route.target.hvnConnection.type
                            "hashicorp.network.hvn"
                          )
                          @isHvnHvnPeering
                          (resourceIsBusy route type="hashicorp.network.route")
                        )
                      )
                    }}
                      {{#unless route.isLocked}}
                        {{#if (can "delete network-route")}}
                          <Flex @justifyContent="end" @direction="row">
                            <HdsDropdown as |dd|>
                              <dd.ToggleIcon
                                @icon="more-horizontal"
                                @text={{t "components.form.more"}}
                                @hasChevron={{false}}
                                data-test-more-menu-trigger
                              />
                              <dd.Interactive
                                @color="critical"
                                @icon="trash"
                                @text={{t "components.form.delete"}}
                                id="modal-dialog-open-button-{{route.id}}"
                                data-test-delete-button
                                {{on
                                  "click"
                                  (queue
                                    (set this "hvnRouteToDelete" route)
                                    (set this "showDeleteModal" true)
                                  )
                                }}
                              />
                            </HdsDropdown>
                          </Flex>
                        {{/if}}
                      {{/unless}}
                    {{/if}}
                  </B.Td>
                </B.Tr>
              {{/each}}
            </:body>
          </HdsTable>
        {{else}}
          <HdsTable
            @valign="middle"
            @caption={{t "components.page.hvn-routes.list.title"}}
            data-test-table
            data-test-hvn-routes-list
          >
            <:head as |H|>
              <H.Tr>
                <H.Th>
                  {{t "components.page.hvn-routes.list.table.headers.id"}}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-routes.list.table.headers.destination"
                  }}
                </H.Th>
                {{#if (eq this.routeName this.hvnRouteNames.list)}}
                  <H.Th>
                    {{t "components.page.hvn-routes.list.table.headers.target"}}
                  </H.Th>
                {{/if}}
                <H.Th>
                  {{t "components.page.hvn-routes.list.table.headers.status"}}
                </H.Th>
                {{#if (eq this.routeName this.hvnRouteNames.list)}}
                  <H.Th>
                    {{t
                      "components.page.hvn-routes.list.table.headers.target-type"
                    }}
                  </H.Th>
                {{/if}}
                <H.Th />
              </H.Tr>
            </:head>
            <:body as |B|>
              {{#each @model.routes key="id" as |route|}}
                <B.Tr data-test-hvn-routes-list-item>
                  <B.Td data-test-hvn-routes-list-item-id>
                    {{#if
                      (eq
                        route.target.hvnConnection.type "hashicorp.network.hvn"
                      )
                    }}
                      {{t
                        "components.page.hvn-routes.list.network-destination-id"
                      }}
                      <HdsIcon
                        @name="info"
                        @isInline={{true}}
                        {{tooltip
                          (t
                            "components.page.hvn-routes.list.tooltip.default-route"
                          )
                          options=(hash placement="top" offset=(array 0 10))
                        }}
                      />
                    {{else}}
                      {{route.id}}
                      {{#if route.isLocked}}
                        <HdsIcon
                          @name="lock-fill"
                          @isInline={{true}}
                          {{tooltip
                            (t
                              "components.page.hvn-routes.list.tooltip.locked-route"
                            )
                            options=(hash placement="top" offset=(array 0 10))
                          }}
                          data-test-peering-locked
                          {{boxPadding "0 0 0 2xs"}}
                        />
                      {{/if}}
                    {{/if}}
                  </B.Td>
                  <B.Td data-test-hvn-routes-list-item-destina>
                    {{route.destination}}
                  </B.Td>
                  {{#if (eq this.routeName this.hvnRouteNames.list)}}
                    <B.Td>
                      {{! TODO there has to be a way to put this into some sort of helper  }}
                      {{#if
                        (eq
                          route.target.hvnConnection.type
                          this.connectionsInfo.tgwAttachment.type
                        )
                      }}
                        <HdsLinkInline
                          @model={{route.target.hvnConnection.id}}
                          @route={{this.connectionsInfo.tgwAttachment.detail}}
                        >
                          {{route.target.hvnConnection.id}}
                        </HdsLinkInline>
                      {{else if
                        (eq
                          route.target.hvnConnection.type
                          this.connectionsInfo.peering.type
                        )
                      }}
                        <HdsLinkInline
                          @model={{route.target.hvnConnection.id}}
                          @route={{this.connectionsInfo.peering.detail}}
                        >
                          {{#if route.isLocked}}
                            <span data-test-federated-peering-target>
                              {{route.target.hvnConnection.id}}
                            </span>
                          {{else}}
                            {{route.target.hvnConnection.id}}
                          {{/if}}
                        </HdsLinkInline>
                      {{else}}
                        {{route.target.hvnConnection.id}}
                      {{/if}}
                    </B.Td>
                  {{/if}}
                  <B.Td>
                    {{#if
                      (eq
                        route.target.hvnConnection.type "hashicorp.network.hvn"
                      )
                    }}
                      {{t
                        "components.page.hvn-routes.list.network-destination-status"
                      }}
                    {{else}}
                      {{#let
                        (optionForSourceState "hvn_route" route.state)
                        as |option|
                      }}
                        <HdsBadge
                          @text={{t option.text}}
                          @color={{option.color}}
                          @icon={{option.icon}}
                          @isIconOnly={{option.isIconOnly}}
                          @size={{option.size}}
                          @type={{option.type}}
                        />
                      {{/let}}
                    {{/if}}
                  </B.Td>
                  {{#if (eq this.routeName this.hvnRouteNames.list)}}
                    <B.Td>
                      <TargetTypeLabel
                        @route={{route}}
                        @network={{@model.network}}
                      />
                    </B.Td>
                  {{/if}}

                  <B.Td>
                    {{#if
                      (not
                        (or
                          (eq
                            route.target.hvnConnection.type
                            "hashicorp.network.hvn"
                          )
                          @isHvnHvnPeering
                          (resourceIsBusy route type="hashicorp.network.route")
                        )
                      )
                    }}
                      {{#unless route.isLocked}}
                        {{#if (can "delete network-route")}}
                          <HdsDropdown as |dd|>
                            <dd.ToggleIcon
                              @icon="more-horizontal"
                              @text={{t "components.form.more"}}
                              @hasChevron={{false}}
                              data-test-more-menu-trigger
                            />
                            <dd.Interactive
                              @color="critical"
                              @icon="trash"
                              @text={{t "components.form.delete"}}
                              id="modal-dialog-open-button-{{route.id}}"
                              data-test-delete-button
                              {{on
                                "click"
                                (queue
                                  (set this "hvnRouteToDelete" route)
                                  (set this "showDeleteModal" true)
                                )
                              }}
                            />
                          </HdsDropdown>
                        {{/if}}
                      {{/unless}}
                    {{/if}}
                  </B.Td>
                </B.Tr>
              {{/each}}
            </:body>
          </HdsTable>
        {{/if}}
        {{! Delete HVN Route  }}
        {{#if this.showDeleteModal}}
          <HdsModal
            @color="critical"
            @onClose={{queue
              (set this "showDeleteModal")
              (set this "allowDelete")
            }}
            data-test-delete-modal
            as |M|
          >
            <M.Header @icon="alert-triangle">
              {{t "components.page.hvn-routes.delete.title"}}
            </M.Header>
            <M.Body>
              <form
                id="delete"
                class="hcp-hds-form"
                {{on "submit" this.deleteHvnRoute.perform}}
              ></form>
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
              >
                {{t
                  "components.page.hvn-routes.delete.warning"
                  routeDestination=this.hvnRouteToDelete.destination
                  connectionName=this.hvnRouteToDelete.target.hvnConnection.id
                  htmlSafe=true
                }}
              </Typography>
            </M.Body>
            <M.Footer as |F|>
              <HdsButtonSet>
                <HdsButton
                  @color="critical"
                  @text={{t "components.form.remove"}}
                  form="delete"
                  type="submit"
                  data-test-confirm-delete
                />
                <HdsButton
                  @color="secondary"
                  @text={{t "components.form.cancel"}}
                  {{on "click" F.close}}
                />
              </HdsButtonSet>
            </M.Footer>
          </HdsModal>
        {{/if}}
      {{else}}
        <div class="hvnsRoutesList__zeroState">
          <ZeroState as |ZS|>
            <ZS.Header>
              {{t "components.page.hvn-routes.list.empty.header"}}
            </ZS.Header>
            <ZS.Message>
              {{this.zeroStateMessage}}
            </ZS.Message>
            <ZS.Action>
              {{! Routes will never be zero in the Routes tab. }}
              {{#if
                (and
                  this.connection
                  (not
                    (or
                      @isHvnHvnPeering
                      (eq this.connection.state "EXPIRED")
                      (eq this.connection.state "REJECTED")
                      (eq this.connection.state "FAILED")
                    )
                  )
                )
              }}
                <HdsLinkInline
                  data-test-zero-state-create
                  @route={{this.connectionCreateHVN}}
                  @model={{this.connection.id}}
                >
                  {{t "components.page.hvn-routes.create.title"}}
                </HdsLinkInline>
              {{/if}}
            </ZS.Action>
          </ZeroState>
        </div>
      {{/if}}
    </div>
  </template>
}
